import React, {Fragment} from "react";
import {useRouter} from "next/router";
import {IAttributeValue} from "../../types/widgetFiltresTypes";
import {GetServerSideProps, NextPage} from "next";
import {layoutProps} from "../../services/layoutProps";
import MainLayout, {ICurrencies, ILayout} from "../../components/ForCWV/Wrappers/MainLayout";
import Head from "next/head";
import {domainUrl} from "../../helper";
import CustomCategoryPage, {IProductList} from "../../components/ForCWV/CustomCategoryPage";
import {TranslationsType} from "../../types/homePageTypes";
import {CategoryPageTranslations} from "../../services/translationKeys";
import {
    getBuilderData,
    getCategoryId,
    getCustomerGroups,
    getFiltersCategory,
    getNewCategoryProducts1,
} from "../../api/pages/catalog";
import ReCaptchaWrapper from "../../components/shared/ReCaptchaWrapper";

interface ICatalogProps extends ILayout {
    brandList: Array<IAttributeValue>;
    productsList: IProductList,
    dbName: string;
    domain: string;
    titleCategory: string;
    categoryDescription: string | TrustedHTML;
    selectedRate: ICurrencies;
    translationsData: TranslationsType;
    breadcrumb: Array<{ title: string; url: string }>;
    blockStyleHandle: string;
    hasFilterInQuery: boolean;
    metaOptions: Record<string, string>;
    selectedLocale: string;
    backOrderValue: string;
    whichOneRender: {
        firstDivClassName: string;
        firstDivParams: { "data-layout": string } | {};
        secondDivClassName: string;
        showEmptyFiltersSection: boolean;
        showElement: "List" | "Empty"
    };
    getPages: Array<number>;
    getBuilderData: any;
    showBuilderData: boolean,
    currencies: Array<ICurrencies>;
    recaptchaActive: string;
    recaptchaKey: string;
    siteName: string
}

const Catalog: NextPage<ICatalogProps> = (pageProps) => {
    const {
        dbName,
        domain,
        productsList,
        titleCategory,
        metaOptions: {
            meta_title = "",
            meta_description = "",
            meta_keywords = "",
            image = "",
            tw_title = "",
            tw_desc = "",
            fb_title = "",
            fb_desc = "",
            y_key = ""
        } = {},
        selectedRate,
        selectedLocale,
        brandList,
        categoryDescription,
        translationsData,
        breadcrumb,
        blockStyleHandle,
        hasFilterInQuery,
        whichOneRender,
        backOrderValue,
        getBuilderData,
        showBuilderData,
        currencies,
        isMobile,
        recaptchaActive,
        recaptchaKey,
        siteName,
        defaultLangCode,
        locales
    } = pageProps
    const {asPath, locale} = useRouter();
    const meta_url = asPath.split("?")[0];
    const canonicalUrl = `${dbName}${locale !== "catchAll" && locale !== defaultLangCode ? "/" + selectedLocale : ""}${meta_url}`

    return (
        <ReCaptchaWrapper recaptchaActive={recaptchaActive} recaptchaKey={recaptchaKey} >
            <MainLayout {...pageProps}>
                <Fragment>
                    <Head>
                        <title>{meta_title}</title>
                        <meta name="title" content={meta_title}/>
                        <meta name="description" content={meta_description}/>
                        <meta name="keywords" content={meta_keywords}/>
                        <meta name="generator" content="Zegashop.com"/>
                        <meta name="robots" content="index, follow"/>
                        <meta name="twitter:title" content={tw_title}/>
                        <meta name="twitter:description" content={tw_desc}/>
                        <meta name="twitter:image" content={domainUrl(`${dbName}/storage/${domain}/${image}`)}/>
                        <meta property="og:title" content={fb_title}/>
                        <meta property="og:description" content={fb_desc}/>
                        <meta property="og:keywords" content={y_key}/>
                        <meta property="og:image" content={domainUrl(`${dbName}/storage/${domain}/${image}`)}/>
                        <meta property="og:url" content={domainUrl(canonicalUrl)}/>
                        <meta property="og:locale" content={selectedLocale}/>
                        <meta property="og:site_name" content={siteName}/>
                        <link rel="canonical" href={domainUrl(canonicalUrl)}/>
                        {locales?.length > 0 ? (
                            <>
                                <link rel="alternate" href={domainUrl(`${dbName}${meta_url}`)} hrefLang="x-default"/>
                                <link rel="alternate" href={domainUrl(`${dbName}${meta_url}`)} hrefLang={defaultLangCode}/>
                                {locales.map(intl => (
                                    <link rel="alternate" href={domainUrl(`${dbName}/${intl.code}${meta_url}`)} hrefLang={intl.code}/>
                                ))}
                            </>
                        ) : null}
                    </Head>
                    <CustomCategoryPage
                        brandList={brandList}
                        productsList={productsList}
                        dbName={dbName}
                        titleCategory={titleCategory}
                        categoryDescription={categoryDescription}
                        selectedRate={selectedRate}
                        translationsData={translationsData}
                        breadcrumb={breadcrumb}
                        blockStyleHandle={blockStyleHandle}
                        hasFilterInQuery={hasFilterInQuery}
                        domain={domain}
                        selectedLocale={selectedLocale}
                        backOrderValue={backOrderValue}
                        whichOneRender={whichOneRender}
                        getBuilderData={getBuilderData}
                        showBuilderData={showBuilderData}
                        currencies={currencies}
                        isMobile={isMobile}
                        recaptchaActive={recaptchaActive}
                    />
                </Fragment>
            </MainLayout>
        </ReCaptchaWrapper>
    )
};
export const getServerSideProps: GetServerSideProps = async ({locale, req, query}) => {
    const {slug, sort_by_price: sortBy, page: currentPage = 1, limit = 12} = query;
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const dbName = req.headers["x-forwarded-host"] as string;
    const models = typedReq.m;
    const domain = typedReq.defaultDB.db_name;
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: CategoryPageTranslations,
        ipv4: typedReq.ipv4,
        forEachPageBuilder: true
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, isMobile, selectedRate, translationsData: {Home: HomeIntl}, themeOptions, backOrderValue, isInfiniteScroll } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }

    const filterValues: Record<string, string> = Object.keys(query)
        .filter(param => param !== "page")
        .reduce((acc: Record<string, string>, param: string) => {
            const mr = param.match(/^filter_([-_A-Za-z0-9]+)$/);
            if (mr) {
                acc[mr[1]] = query[param] as string;
            }
            return acc;
        }, {});
    let categoryId: number | undefined;
    let categoryDescription: string | undefined;
    let metaOptions: any = {
        meta_title: "",
        meta_description: "",
        meta_keywords: "",
        image: "configuration/logo/logo.webp",
        tw_title: "",
        tw_desc: "",
        fb_title: "",
        fb_desc: "",
        y_key: ""
    };
    let titleCategory = "";

    function getCategoryDetails(array: any[]) {
        array?.forEach((
            {
                yoast: [{
                    yoast_twitter_title: tw_title = "",
                    yoast_twitter_description: tw_desc = "",
                    yoast_facebook_title: fb_title = "",
                    yoast_facebook_description: fb_desc = "",
                    yoast_focus_keyword: y_key = ""
                } = {}] = [],
                ...e
            }
        ) => {
            if (e?.slug === slug) {
                titleCategory = e?.name
                categoryId = e?.id;
                categoryDescription = e?.description
                metaOptions = {
                    meta_title: !!e?.meta_title ? e?.meta_title : e?.name || "",
                    meta_description: e?.meta_description || "",
                    meta_keywords: e?.meta_keywords || "",
                    image: e?.image ? e?.image : "configuration/logo/logo.webp",
                    tw_title: !!tw_title ? tw_title : !!e?.meta_title ? e?.meta_title : e?.name || "",
                    tw_desc: !!tw_desc ? tw_desc : e?.meta_description || "",
                    fb_title: !!fb_title ? fb_title : !!e?.meta_title ? e?.meta_title : e?.name || "",
                    fb_desc: !!fb_desc ? fb_desc : e?.meta_description || "",
                    y_key: !!y_key ? y_key : e?.meta_keywords || "",
                };
            }
            if (e?.slug == slug && e?.children?.length === 0) {
                categoryId = e?.id;
                return false;
            } else {
                getCategoryDetails(e?.children);
            }
        });
    }

    getCategoryDetails(wrapperProps?.categories?.[0]?.children);
    const {notFound: brandNotFound = 0, ...brands} = await getFiltersCategory(categoryId as number, models, selectedLocale)
    const { id = 0 } = await getCategoryId(slug as string, models) || {};
    const getBuilderDataServer = await getBuilderData(id + 100, selectedLocale, dbName, domain, selectedRate, models, isMobile, themeOptions) || []
    let responseProductList = {data: [], total: 0, pageCount: 1, emptyCategory: false, notFound: false};
    if (isInfiniteScroll) {
        const arr = Array.from({ length: Number(currentPage) }, (_, i) => i + 1);
        for (const eachPage of arr) {
            responseProductList = await getNewCategoryProducts1(
                slug as string, selectedLocale, limit, eachPage, selectedRate,
                sortBy === "asc" ? 1 : sortBy === "desc" ? -1 : null,
                isInfiniteScroll, filterValues, isMobile, backOrderValue, responseProductList.data, models
            )
        }
    } else {
        responseProductList = await getNewCategoryProducts1(
            slug as string, selectedLocale, limit, currentPage, selectedRate,
            sortBy === "asc" ? 1 : sortBy === "desc" ? -1 : null,
            isInfiniteScroll, filterValues, isMobile, backOrderValue, [], models
        ) || {data: [], total: 0, pageCount: 1};
    }

    responseProductList.emptyCategory = false;
    if (responseProductList?.notFound) {
        responseProductList.data = [];
        responseProductList.emptyCategory = true;
    }
    const customerGroupPrices = await getCustomerGroups(models)
    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: titleCategory, url: "/catalog"}
    ];
    const hasFilterInQuery = responseProductList?.data.length === 0
        && !Object.keys(query).some((lll) => lll.includes("filter"))
    let whichOneRender = {
        firstDivClassName: "products-view__empty",
        firstDivParams: {},
        secondDivClassName: "products-view__empty-title",
        showEmptyFiltersSection: true,
        showElement: "Empty"
    };
    if (responseProductList.data?.length > 0) {
        whichOneRender = {
            firstDivClassName: "products-list",
            firstDivParams: {"data-layout": "grid-3-sidebar"},
            secondDivClassName: "products-list__body",
            showEmptyFiltersSection: false,
            showElement: "List"
        }
    }
    const brandList: IAttributeValue[] = Object.values(JSON.parse(JSON.stringify(brands)))
    const priceAttr = brandList.find((item) => item?.attributes?.[0]?.code === "price");
    const finalBrandList = brandList.filter((item) => item?.attribute_id !== priceAttr?.attribute_id);
    finalBrandList.push(priceAttr as IAttributeValue);
    if (query?.cat_id || query?.currencies || String(query?.page) === "1") {
        return { notFound: true }
    }

    return {
        props: {
            ...wrapperProps,
            productsList: responseProductList,
            brandList: finalBrandList?.filter(Boolean),
            titleCategory,
            metaOptions,
            categoryDescription: categoryDescription ?? null,
            getBuilderData: getBuilderDataServer,
            showBuilderData: Object.values(getBuilderDataServer).some((lll) => (lll as Array<any>)?.length > 0),
            customerGroupPrices: customerGroupPrices ?? [],
            notFound: !!brandNotFound,
            breadcrumb,
            blockStyleHandle: hasFilterInQuery ? "empty_block" : "full_block",
            hasFilterInQuery,
            whichOneRender,
            isMobile
        },
    };
}

export default Catalog
